import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { BookOpen, Info, Lightbulb, ListChecks, MousePointerClick, X } from 'lucide-react';
import { getPageHelp } from '../../content/pageHelp';
import { cn } from '../../lib/utils';

interface PageHelpProps {
  /** Explicit content key. Defaults to the current route path. */
  path?: string;
  /** Extra classes applied to the trigger button. */
  className?: string;
}

/** Section headings for the English view. */
const ENGLISH_LABELS = {
  overview: 'Overview',
  features: 'What you can do',
  steps: 'How to use',
  tips: 'Good to know',
  dismiss: 'Got it',
} as const;

/** Section headings for the Bangla (বাংলা) view. */
const BANGLA_LABELS = {
  overview: 'সারসংক্ষেপ',
  features: 'আপনি কী করতে পারবেন',
  steps: 'কীভাবে ব্যবহার করবেন',
  tips: 'জেনে রাখুন',
  dismiss: 'বুঝেছি',
} as const;

/**
 * Small info icon rendered next to a page title. Clicking it opens a modal
 * describing the page's purpose, capabilities, step-by-step usage and notes.
 *
 * Content is resolved from the central `PAGE_HELP` registry by route (or an
 * explicit `path`), so pages only need to drop `<PageHelp />` into their header.
 * Renders nothing when no content is registered for the route.
 */
export function PageHelp({ path, className }: PageHelpProps) {
  const location = useLocation();
  const content = getPageHelp(path ?? location.pathname);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<'en' | 'bn'>('en');

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!content) return null;

  const hasBangla = Boolean(content.bn);
  const activeLang: 'en' | 'bn' = hasBangla ? lang : 'en';
  const active = activeLang === 'bn' && content.bn ? content.bn : content;
  const labels = activeLang === 'bn' ? BANGLA_LABELS : ENGLISH_LABELS;

  const modal = (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="page-help-title"
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Info size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 id="page-help-title" className="text-lg font-semibold text-text">
                {active.title}
              </h2>
              {active.subtitle && (
                <p className="text-sm text-text-muted">{active.subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Language toggle */}
        {hasBangla && (
          <div className="flex items-center gap-1 border-b border-border px-5 py-2">
            <button
              type="button"
              onClick={() => setLang('en')}
              className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                activeLang === 'en' ? 'bg-primary/10 text-primary' : 'text-text-muted hover:text-text'
              }`}
            >
              English
            </button>
            <button
              type="button"
              onClick={() => setLang('bn')}
              lang="bn"
              className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                activeLang === 'bn' ? 'bg-primary/10 text-primary' : 'text-text-muted hover:text-text'
              }`}
            >
              বাংলা
            </button>
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto" lang={activeLang === 'bn' ? 'bn' : undefined}>
          {/* Overview */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-text uppercase tracking-wide">{labels.overview}</h3>
            </div>
            <p className="text-sm text-text-muted leading-relaxed">{active.overview}</p>
          </section>

          {/* Features */}
          {active.features.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <ListChecks size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-text uppercase tracking-wide">
                  {labels.features}
                </h3>
              </div>
              <ul className="space-y-1.5">
                {active.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-text">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Steps */}
          {active.steps.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <MousePointerClick size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-text uppercase tracking-wide">
                  {labels.steps}
                </h3>
              </div>
              <ol className="space-y-2">
                {active.steps.map((step, index) => (
                  <li key={step} className="flex items-start gap-3 text-sm text-text">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold shrink-0">
                      {index + 1}
                    </span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Tips */}
          {active.tips && active.tips.length > 0 && (
            <section className="rounded-lg border border-warning/30 bg-warning/5 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb size={16} className="text-warning" />
                <h3 className="text-sm font-semibold text-text uppercase tracking-wide">
                  {labels.tips}
                </h3>
              </div>
              <ul className="space-y-1.5">
                {active.tips.map((tip) => (
                  <li key={tip} className="flex items-start gap-2 text-sm text-text-muted">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-warning shrink-0" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            {labels.dismiss}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`About: ${content.title}`}
        aria-label={`About ${content.title}`}
        className={cn(
          'inline-flex items-center justify-center w-5 h-5 rounded-full text-text-muted hover:text-primary hover:bg-primary/10 transition-colors shrink-0',
          className,
        )}
      >
        <Info size={15} />
      </button>
      {open && createPortal(modal, document.body)}
    </>
  );
}
